import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { formatFCFA, STATUS_LABELS, STATUS_COLORS, imageUrl } from "../../lib/format";
import { SOURCE_LABELS } from "../../lib/source";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { MessageCircle, Truck } from "lucide-react";

const clientWaLink = (o, kind = "confirm") => {
  let num = String(o.client?.telephone || "").replace(/\D/g, "").replace(/^00/, "");
  if (num.length === 10) num = `225${num}`;
  const prenom = (o.client?.nom || "").trim().split(/\s+/)[0] || "";
  const text = kind === "ship"
    ? `Bonjour ${prenom}, bonne nouvelle : votre commande n°${o.numero} chez Le Dressing de Yoyo est en route ! Le livreur vous contactera très bientôt. Merci pour votre confiance.`
    : `Bonjour ${prenom}, merci pour votre commande n°${o.numero} chez Le Dressing de Yoyo. Elle a bien été enregistrée (total : ${formatFCFA(o.total)}). Nous revenons vers vous très vite pour finaliser le paiement et la livraison.`;
  return `https://wa.me/${num}?text=${encodeURIComponent(text)}`;
};

const FLOW = ["en_attente_paiement", "paiement_confirme", "en_preparation", "expediee", "livree", "annulee"];

export default function AdminOrders() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [statut, setStatut] = useState("all");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);

  const load = () => {
    api.get("/admin/orders", { params: {
      limit: 50,
      ...(statut !== "all" ? { statut } : {}),
      ...(search ? { search } : {}),
    }}).then((r) => { setItems(r.data?.items || []); setTotal(r.data?.total || 0); });
  };

  useEffect(() => { load(); }, [statut]);
  useEffect(() => { const t = setTimeout(load, 400); return () => clearTimeout(t); }, [search]);

  const changeStatus = async (order, newStatut) => {
    await api.put(`/admin/orders/${order.id}/status`, { statut: newStatut });
    toast.success("Statut mis à jour");
    load();
    setDetail((d) => d ? { ...d, statut: newStatut } : d);
  };

  const confirmPayment = async (order) => {
    const ref = window.prompt("Référence / preuve du paiement Wave (optionnel) :", order.paiement?.reference || "");
    await api.put(`/admin/orders/${order.id}/paiement`, { reference: ref || "" });
    toast.success("Paiement confirmé");
    load();
    setDetail((d) => d ? { ...d, statut: "paiement_confirme" } : d);
  };

  const restock = async (order) => {
    const { data } = await api.put(`/admin/orders/${order.id}/restock`);
    toast.success(`${data.restocked} pièce(s) remise(s) en stock`);
  };

  return (
    <div>
      <h1 className="mb-6 font-serif text-3xl font-medium text-[#2C1A14]">Commandes <span className="text-base text-[#9B7E73]">({total})</span></h1>

      <div className="mb-4 flex flex-wrap gap-3">
        <Input placeholder="Nom, téléphone, n° commande..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="order-search" className="max-w-xs bg-white" />
        <Select value={statut} onValueChange={setStatut}>
          <SelectTrigger data-testid="order-filter-statut" className="w-56 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {FLOW.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-hidden rounded-xl bg-white ring-1 ring-[#E8D5CE]">
        <table className="w-full text-sm">
          <thead className="border-b border-[#E8D5CE] bg-[#FBF1EE] text-left text-xs uppercase text-[#9B7E73]">
            <tr>
              <th className="p-3">N°</th>
              <th className="p-3">Client</th>
              <th className="hidden p-3 sm:table-cell">Commune</th>
              <th className="p-3">Total</th>
              <th className="p-3">Statut</th>
            </tr>
          </thead>
          <tbody>
            {items.map((o) => (
              <tr key={o.id} data-testid={`order-row-${o.id}`} onClick={() => setDetail(o)} className="cursor-pointer border-b border-[#F0E4DE] last:border-0 hover:bg-[#FBF1EE]">
                <td className="p-3 font-medium text-[#2C1A14]">{o.numero}</td>
                <td className="p-3">
                  <p className="text-[#2C1A14]">{o.client?.nom}</p>
                  <p className="flex items-center gap-2 text-xs text-[#9B7E73]">
                    {o.client?.telephone}
                    <a href={clientWaLink(o)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} title="Confirmer par WhatsApp" data-testid={`order-wa-${o.id}`} className="rounded-full p-1 text-[#25D366] hover:bg-[#E8F8EE]">
                      <MessageCircle className="h-4 w-4" />
                    </a>
                  </p>
                </td>
                <td className="hidden p-3 text-[#6E544A] sm:table-cell">{o.client?.commune}</td>
                <td className="p-3 text-[#D76E4D]">{formatFCFA(o.total)}</td>
                <td className="p-3">
                  <Badge className={`border ${STATUS_COLORS[o.statut]}`}>{STATUS_LABELS[o.statut]}</Badge>
                  {o.paiement?.preuve && <span data-testid={`order-proof-badge-${o.id}`} className="ml-1 inline-block rounded-full bg-[#E8F8EE] px-2 py-0.5 text-[10px] font-semibold text-[#1da851]">Preuve reçue</span>}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[#9B7E73]">Aucune commande</td></tr>}
          </tbody>
        </table>
      </div>

      <Sheet open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <SheetContent className="w-full overflow-y-auto bg-[#FBF1EE] sm:max-w-md">
          {detail && (
            <>
              <SheetHeader><SheetTitle className="font-serif text-2xl text-[#2C1A14]">Commande {detail.numero}</SheetTitle></SheetHeader>
              <div className="mt-6 space-y-5">
                <div className="rounded-lg bg-white p-4 ring-1 ring-[#E8D5CE]">
                  <p className="text-xs uppercase text-[#9B7E73]">Client</p>
                  <p className="font-medium text-[#2C1A14]">{detail.client?.nom}</p>
                  <p className="text-sm text-[#6E544A]">{detail.client?.telephone}</p>
                  <p className="text-sm text-[#6E544A]">{detail.client?.commune} — {detail.client?.adresse}</p>
                  <p className="mt-1 text-xs text-[#9B7E73]" data-testid="order-source">Venue via : <strong className="text-[#2C1A14]">{SOURCE_LABELS[detail.source] || "Inconnue"}</strong></p>
                  <a href={clientWaLink(detail)} target="_blank" rel="noopener noreferrer" data-testid="confirm-whatsapp-button"
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#1EBE5A]">
                    <MessageCircle className="h-4 w-4" /> Confirmer par WhatsApp
                  </a>
                  <a href={clientWaLink(detail, "ship")} target="_blank" rel="noopener noreferrer" data-testid="ship-whatsapp-button"
                    className="mt-2 flex w-full items-center justify-center gap-2 rounded-full border border-[#25D366] bg-white px-4 py-2 text-sm font-medium text-[#1da851] transition-colors hover:bg-[#E8F8EE]">
                    <Truck className="h-4 w-4" /> Colis en route (WhatsApp)
                  </a>
                </div>

                <div className="rounded-lg bg-white p-4 ring-1 ring-[#E8D5CE]" data-testid="order-proof">
                  <p className="mb-2 text-xs uppercase text-[#9B7E73]">Preuve de paiement</p>
                  {detail.paiement?.preuve ? (
                    <a href={imageUrl(detail.paiement.preuve)} target="_blank" rel="noopener noreferrer" data-testid="order-proof-link">
                      <img src={imageUrl(detail.paiement.preuve)} alt="Preuve de paiement" className="max-h-80 w-full rounded-lg object-contain ring-1 ring-[#E8D5CE]" />
                      <p className="mt-1 text-xs text-[#9B7E73]">Reçue le {detail.paiement.preuve_at ? new Date(detail.paiement.preuve_at).toLocaleString("fr-FR") : ""} · cliquer pour agrandir</p>
                    </a>
                  ) : <p className="text-sm text-[#9B7E73]">Aucune preuve envoyée pour l'instant.</p>}
                </div>

                <div className="rounded-lg bg-white p-4 ring-1 ring-[#E8D5CE]">
                  <p className="mb-2 text-xs uppercase text-[#9B7E73]">Articles</p>
                  {(detail.items || []).map((it, i) => (
                    <div key={it.product_id || i} className="flex justify-between border-b border-[#F0E4DE] py-2 text-sm last:border-0">
                      <span className="text-[#2C1A14]">{it.nom} ({it.taille}) ×{it.quantite}</span>
                      <span className="text-[#6E544A]">{formatFCFA(it.prix_unitaire * it.quantite)}</span>
                    </div>
                  ))}
                  <div className="mt-2 flex justify-between text-sm text-[#6E544A]"><span>Livraison</span><span>{formatFCFA(detail.frais_livraison)}</span></div>
                  <div className="mt-1 flex justify-between font-semibold text-[#2C1A14]"><span>Total</span><span>{formatFCFA(detail.total)}</span></div>
                </div>

                <div>
                  <p className="mb-2 text-xs uppercase text-[#9B7E73]">Statut de la commande</p>
                  <Select value={detail.statut} onValueChange={(v) => changeStatus(detail, v)}>
                    <SelectTrigger data-testid="admin-order-status-select" className="bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FLOW.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {detail.statut === "en_attente_paiement" && (
                    <Button onClick={() => confirmPayment(detail)} data-testid="confirm-payment-button" className="mt-3 w-full rounded-full bg-[#4A7C59] text-white hover:bg-[#3d6449]">
                      Marquer paiement confirmé
                    </Button>
                  )}
                  {detail.statut === "annulee" && (
                    <Button onClick={() => restock(detail)} data-testid="restock-button" className="mt-3 w-full rounded-full bg-[#2C1A14] text-white hover:bg-[#3d251c]">
                      Remettre le(s) produit(s) en stock
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
